import { Card, CardContent } from "@/components/ui/card";
import type { EmployeeBf } from "@/hooks/use-employee-bf";
import { formatNOK } from "@/lib/format";
import { TONE_BG, TONE_PILL } from "@/lib/widget-status";
import { cn } from "@/lib/utils";

interface BfMaalSectionProps {
  data: EmployeeBf;
  year: number;
}

/**
 * BF-seksjonen på profilsidens MaalTab (VIB-632). Målsemantikk låst i
 * VIB-1285: målet og oppnådd-statusen er ENHETENS og deles av alle medlemmer;
 * personens tall presenteres som BIDRAG — aldri individuell måloppnåelse.
 * Bidraget i kroner står i nøkkeltallene over (VIB-3028).
 */
export function BfMaalSection({ data, year }: BfMaalSectionProps) {
  if (!data.hasUnit) {
    return (
      <Card>
        <CardContent className="p-5">
          <h2 className="mb-2 font-display text-base font-semibold">GEEK-mål {year}</h2>
          <p className="text-sm text-muted-foreground">
            Ingen GEEK-mål, ikke medlem av noen selgergruppe i {year}.
          </p>
        </CardContent>
      </Card>
    );
  }

  const unitPct = data.targetKr > 0 ? (data.unitLevertKr / data.targetKr) * 100 : 0;
  const bidragPct = data.targetKr > 0 ? (data.ytdBidragKr / data.targetKr) * 100 : 0;

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3">
          <h2 className="font-display text-base font-semibold">
            GEEK-mål {year} · {data.unitNames.join(" + ")}
          </h2>
          <span
            className={cn(
              "rounded-full px-2.5 py-0.5 text-xs font-medium",
              data.achieved ? TONE_PILL.reached : "bg-secondary text-foreground",
            )}
          >
            {data.achieved ? "Enhetens mål nådd" : "Ikke nådd ennå"}
          </span>
        </div>
        <div className="space-y-2 border-t border-border px-5 py-4">
          <div className="flex justify-between gap-3 text-sm">
            <span className="text-muted-foreground">Enhetens oppnåelse (delt mål)</span>
            <span className="tabular-nums">
              {formatNOK(data.unitLevertKr)} / {formatNOK(data.targetKr)} ({Math.round(unitPct)} %)
            </span>
          </div>
          {/* Stablet bar: lys = enhetens samlede oppnåelse, mørk = personens bidrag */}
          <div className="relative h-2.5 overflow-hidden rounded-full bg-muted" aria-hidden="true">
            <div className={cn("absolute inset-y-0 left-0 opacity-30", TONE_BG.reached)} style={{ width: `${Math.min(unitPct, 100)}%` }} />
            <div className={cn("absolute inset-y-0 left-0", TONE_BG.reached)} style={{ width: `${Math.min(bidragPct, 100)}%` }} />
          </div>
          <p className="text-xs text-muted-foreground">
            Mørk del er den ansattes bidrag. Målet er enhetens og gjelder alle medlemmene likt.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
